import type { DesignElement } from '@canvas/shared-types';
import { svgDataUrl } from '@/lib/svgDataUrl';

const COLORS = ['#0d7377', '#1b4d89', '#e5c07b', '#45566e', '#9fd4d6'];

function barChartSvg(values: number[], labels: string[]): string {
  const w = 420;
  const h = 280;
  const pad = 36;
  const max = Math.max(...values, 1);
  const gap = 16;
  const barW = (w - pad * 2 - gap * (values.length - 1)) / values.length;

  const bars = values
    .map((v, i) => {
      const bh = ((h - pad * 2) * v) / max;
      const x = pad + i * (barW + gap);
      const y = h - pad - bh;
      return `
        <rect x="${x}" y="${y}" width="${barW}" height="${bh}" rx="4" fill="${COLORS[i % COLORS.length]}"/>
        <text x="${x + barW / 2}" y="${h - 12}" text-anchor="middle" font-size="11" fill="#45566e" font-family="Source Sans 3, Arial">${labels[i]}</text>
      `;
    })
    .join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
    <rect width="${w}" height="${h}" rx="8" fill="#f7f9fc"/>
    <line x1="${pad}" y1="${h - pad}" x2="${w - pad}" y2="${h - pad}" stroke="#c5d0dc"/>
    ${bars}
  </svg>`;
}

function pieChartSvg(values: number[]): string {
  const size = 280;
  const cx = size / 2;
  const cy = size / 2;
  const r = 100;
  const total = values.reduce((a, b) => a + b, 0) || 1;
  let angle = -Math.PI / 2;

  const slices = values
    .map((v, i) => {
      const slice = (v / total) * Math.PI * 2;
      const x1 = cx + r * Math.cos(angle);
      const y1 = cy + r * Math.sin(angle);
      angle += slice;
      const x2 = cx + r * Math.cos(angle);
      const y2 = cy + r * Math.sin(angle);
      const large = slice > Math.PI ? 1 : 0;
      return `<path d="M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z" fill="${COLORS[i % COLORS.length]}"/>`;
    })
    .join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <rect width="${size}" height="${size}" rx="8" fill="#f7f9fc"/>
    ${slices}
    <circle cx="${cx}" cy="${cy}" r="42" fill="#f7f9fc"/>
  </svg>`;
}

function lineChartSvg(values: number[]): string {
  const w = 420;
  const h = 260;
  const pad = 36;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const stepX = (w - pad * 2) / Math.max(values.length - 1, 1);

  const points = values
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = h - pad - ((v - min) / span) * (h - pad * 2);
      return `${x},${y}`;
    })
    .join(' ');

  const dots = values
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = h - pad - ((v - min) / span) * (h - pad * 2);
      return `<circle cx="${x}" cy="${y}" r="4" fill="#0d7377"/>`;
    })
    .join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
    <rect width="${w}" height="${h}" rx="8" fill="#f7f9fc"/>
    <polyline fill="none" stroke="#0d7377" stroke-width="3" points="${points}"/>
    ${dots}
  </svg>`;
}

function chartEl(
  id: string,
  name: string,
  svg: string,
  tags: string[],
  audience: DesignElement['audience'] = 'all',
  w = 360,
  h = 240
): DesignElement {
  const url = svgDataUrl(svg);
  return {
    elementId: id,
    name,
    kind: 'illustration',
    category: 'charts',
    tags,
    audience,
    license: 'free',
    preview: { mode: 'image', value: url, label: name },
    defaultWidth: w,
    defaultHeight: h,
    factory: { type: 'image-url', url },
  };
}

export const CHART_CATALOG: DesignElement[] = [
  chartEl(
    'chart-bar-grades',
    'Bar · Grades',
    barChartSvg([72, 85, 64, 91], ['Q1', 'Q2', 'Q3', 'Q4']),
    ['bar', 'grades', 'school'],
    'student'
  ),
  chartEl(
    'chart-bar-revenue',
    'Bar · Revenue',
    barChartSvg([40, 55, 48, 70, 62], ['Jan', 'Feb', 'Mar', 'Apr', 'May']),
    ['bar', 'revenue', 'business'],
    'professional'
  ),
  chartEl(
    'chart-pie-share',
    'Pie · Share',
    pieChartSvg([35, 25, 20, 20]),
    ['pie', 'share'],
    'all',
    240,
    240
  ),
  chartEl(
    'chart-line-growth',
    'Line · Growth',
    lineChartSvg([12, 18, 16, 28, 34, 40]),
    ['line', 'growth', 'trend'],
    'professional'
  ),
  chartEl(
    'chart-bar-attendance',
    'Bar · Attendance',
    barChartSvg([90, 85, 88, 92], ['Wk1', 'Wk2', 'Wk3', 'Wk4']),
    ['bar', 'attendance', 'class'],
    'student'
  ),
];
